import React from 'react';
import { useLanguage } from '../../contexts/LanguageContext.js';
import { 
  UsersIcon,
  BuildingOfficeIcon,
  UserIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';

const ClientStats = ({ clients }) => {
  const { t } = useLanguage();

  // Calculate client statistics
  const calculateClientStats = () => {
    const totalClients = clients.length;
    const companiesCount = clients.filter(client => client.company && client.company.trim() !== '').length;
    const individualClients = totalClients - companiesCount;
    const recentClients = clients.filter(client => {
      const clientDate = new Date(client.createdAt || Date.now());
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      return clientDate >= thirtyDaysAgo;
    }).length;

    return [
      {
        name: t('totalClients'),
        value: totalClients.toString(),
        change: `${recentClients} ${t('newThisMonth')}`,
        changeType: recentClients > 0 ? 'positive' : 'neutral',
        icon: UsersIcon,
        iconColor: 'bg-purple-500'
      },
      {
        name: t('companies'),
        value: companiesCount.toString(),
        change: `${Math.round((companiesCount / totalClients) * 100) || 0}% ${t('ofTotal')}`,
        changeType: companiesCount > 0 ? 'positive' : 'neutral',
        icon: BuildingOfficeIcon,
        iconColor: 'bg-green-500'
      },
      {
        name: t('individuals'),
        value: individualClients.toString(),
        change: `${Math.round((individualClients / totalClients) * 100) || 0}% ${t('ofTotal')}`,
        changeType: 'neutral',
        icon: UserIcon,
        iconColor: 'bg-blue-500'
      },
      {
        name: t('clientGrowth'),
        value: `+${recentClients}`,
        change: t('last30Days'),
        changeType: recentClients > 0 ? 'positive' : 'neutral',
        icon: ChartBarIcon,
        iconColor: 'bg-orange-500'
      }
    ];
  };

  const stats = calculateClientStats();

  return (
    <div className="mb-8 overflow-x-auto pb-2">
      <div className="flex gap-4 min-w-max md:grid md:grid-cols-2 lg:grid-cols-4 md:min-w-0">
        {stats.map((stat) => {
          const Icon = stat.icon;
          return (
            <div key={stat.name} className="bg-white overflow-hidden shadow rounded-lg w-48 flex-shrink-0 md:w-auto">
              <div className="p-4 md:p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className={`w-8 h-8 ${stat.iconColor} rounded-md flex items-center justify-center`}>
                      <Icon className="w-5 h-5 text-white" aria-hidden="true" />
                    </div>
                  </div>
                  <div className="ml-4 md:ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-xs md:text-sm font-medium text-gray-500 truncate">{stat.name}</dt>
                      <dd className="text-base md:text-lg font-medium text-gray-900">{stat.value}</dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ClientStats;
